import React from 'react';
import { Link } from '@mui/material';

import { MerchantAwareLink, MerchantAwareLinkProps } from '../components/core/MerchantAwareLink';
import type { ConfigResponse } from '../store/modules/merchant/types';
import { configurationPaths } from '../util/constants';
import { getConfigDisplayLabel } from '../util/parentSettingSave';

const CONFIG_SETTING_PATH: Record<string, MerchantAwareLinkProps['to']> = {
  PARTIAL_PAYMENTS: configurationPaths.PAYMENTS_AND_PROCESSING,
  PARTIAL_AUTHORIZATIONS: configurationPaths.PAYMENTS_AND_PROCESSING,
  SPLIT_PAYMENTS: configurationPaths.PAYMENTS_AND_PROCESSING,
  GENERAL_FEE: configurationPaths.FEES,
  SURCHARGE_FEE: configurationPaths.FEES,
  DISCOUNTS: configurationPaths.FEES,
  TIPPING: configurationPaths.TIPPING,
  TIPPING_PERCENTAGES: configurationPaths.TIPPING,
};

const CONFIG_SETTING_LINK_LABEL: Partial<Record<string, string>> = {
  PARTIAL_PAYMENTS: 'Allow Partial Payments',
  PARTIAL_AUTHORIZATIONS: 'Allow Partial Authorization',
  SPLIT_PAYMENTS: 'Allow Split Payments',
};

function ConfigSettingLink({ name }: { name: string }) {
  const to = CONFIG_SETTING_PATH[name] ?? configurationPaths.PAYMENTS_AND_PROCESSING;
  const label =
    CONFIG_SETTING_LINK_LABEL[name] ?? getConfigDisplayLabel(name as ConfigResponse['name']);

  return (
    <Link
      component={MerchantAwareLink}
      to={to}
      underline="always"
      sx={{ fontWeight: 600, color: 'inherit' }}
    >
      {label}
    </Link>
  );
}

/** Fees / tipping / discounts blocked because this merchant owns partial payments. */
export function BlockedByPartialPaymentsMessage({ blockedLabel }: { blockedLabel: string }) {
  return (
    <>
      {blockedLabel} cannot be enabled while <ConfigSettingLink name="PARTIAL_PAYMENTS" /> is on.
      Turn off <ConfigSettingLink name="PARTIAL_PAYMENTS" /> first.
    </>
  );
}

export function PartialAuthBlockedByPartialPaymentsMessage() {
  return (
    <>
      Partial Authorization cannot be enabled while <ConfigSettingLink name="PARTIAL_PAYMENTS" /> is
      on. Turn off <ConfigSettingLink name="PARTIAL_PAYMENTS" /> first.
    </>
  );
}

export function SplitPaymentsBlockedByPartialPaymentsMessage() {
  return (
    <>
      Split Payments cannot be enabled while <ConfigSettingLink name="PARTIAL_PAYMENTS" /> is on.
      Turn off <ConfigSettingLink name="PARTIAL_PAYMENTS" /> first.
    </>
  );
}

/** Allow Partial Payments toggle blocked by another active setting. */
export function PartialPaymentsEnableBlockedMessage({ conflict }: { conflict: string | null }) {
  if (conflict === 'PARTIAL_AUTHORIZATIONS') {
    return (
      <>
        Allow Partial Payments cannot be enabled while{' '}
        <ConfigSettingLink name="PARTIAL_AUTHORIZATIONS" /> is on. Turn off{' '}
        <ConfigSettingLink name="PARTIAL_AUTHORIZATIONS" /> first.
      </>
    );
  }

  if (conflict === 'SPLIT_PAYMENTS') {
    return (
      <>
        Allow Partial Payments cannot be enabled while <ConfigSettingLink name="SPLIT_PAYMENTS" />{' '}
        is on. Turn off <ConfigSettingLink name="SPLIT_PAYMENTS" /> first.
      </>
    );
  }

  if (conflict && CONFIG_SETTING_PATH[conflict]) {
    return (
      <>
        Allow Partial Payments cannot be enabled while <ConfigSettingLink name={conflict} /> is on.
        Turn that setting off first.
      </>
    );
  }

  return (
    <>
      Allow Partial Payments cannot be enabled while{' '}
      <ConfigSettingLink name="PARTIAL_AUTHORIZATIONS" /> or{' '}
      <ConfigSettingLink name="SPLIT_PAYMENTS" /> is on. Turn those options off first.
    </>
  );
}

export function PartialPaymentsSaveConflictMessage({
  enabling,
  conflicting,
}: {
  enabling: string;
  conflicting: string;
}) {
  return (
    <>
      Disable <ConfigSettingLink name={conflicting} /> before enabling{' '}
      <ConfigSettingLink name={enabling} />.
    </>
  );
}
